import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { TrendingUp, Plus, Star } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRPerformance = () => {
  const { getAuthHeader } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    review_period: '',
    rating: 3,
    strengths: '',
    areas_of_improvement: '',
    goals: '',
    comments: ''
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/employees`, {
        headers: getAuthHeader()
      });
      setEmployees(response.data.filter(e => e.is_active));
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/hr/performance`,
        formData,
        { headers: getAuthHeader() }
      );
      alert('Performance review created successfully!');
      setShowForm(false);
      setFormData({
        employee_id: '',
        review_period: '',
        rating: 3,
        strengths: '',
        areas_of_improvement: '',
        goals: '',
        comments: ''
      });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create review');
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 3.5) return 'text-blue-600';
    if (rating >= 2.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp size={28} />
            Performance Management
          </h2>
          <p className="text-gray-600 mt-1">Track and review employee performance</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus size={18} className="mr-2" />
          {showForm ? 'Cancel' : 'New Review'}
        </Button>
      </div>

      {/* Create Review Form */}
      {showForm && (
        <Card className="border-primary">
          <CardHeader className="bg-primary/5">
            <CardTitle>Create Performance Review</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Employee *</label>
                  <select
                    required
                    value={formData.employee_id}
                    onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.employee_id} value={emp.employee_id}>
                        {emp.full_name} ({emp.employee_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Review Period *</label>
                  <input
                    type="text"
                    required
                    value={formData.review_period}
                    onChange={(e) => setFormData({...formData, review_period: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="e.g., Q1-2024, Annual-2024"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Rating (1-5) *</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.5"
                    required
                    value={formData.rating}
                    onChange={(e) => setFormData({...formData, rating: parseFloat(e.target.value)})}
                    className="flex-1"
                  />
                  <div className="text-2xl font-bold w-16 text-center flex items-center gap-1">
                    {formData.rating}
                    <Star size={20} className="text-yellow-500 fill-yellow-500" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Strengths *</label>
                <Textarea
                  required
                  value={formData.strengths}
                  onChange={(e) => setFormData({...formData, strengths: e.target.value})}
                  rows={3}
                  placeholder="Key strengths and achievements..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Areas of Improvement *</label>
                <Textarea
                  required
                  value={formData.areas_of_improvement}
                  onChange={(e) => setFormData({...formData, areas_of_improvement: e.target.value})}
                  rows={3}
                  placeholder="Areas that need development..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Goals for Next Period *</label>
                <Textarea
                  required
                  value={formData.goals}
                  onChange={(e) => setFormData({...formData, goals: e.target.value})}
                  rows={3}
                  placeholder="Goals and objectives..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Additional Comments</label>
                <Textarea
                  value={formData.comments}
                  onChange={(e) => setFormData({...formData, comments: e.target.value})}
                  rows={2}
                  placeholder="Any additional feedback..."
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  Create Review
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h4 className="font-semibold mb-2">Performance Review Guidelines</h4>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>• <strong>Rating Scale:</strong> 1 (Needs Improvement) to 5 (Outstanding)</li>
            <li>• Conduct regular reviews (Quarterly or Annual)</li>
            <li>• Be specific in feedback and examples</li>
            <li>• Set SMART goals (Specific, Measurable, Achievable, Relevant, Time-bound)</li>
            <li>• Schedule follow-up meetings to discuss reviews</li>
          </ul>
        </CardContent>
      </Card>

      {/* Employee List for Quick Access */}
      <Card>
        <CardHeader>
          <CardTitle>Employees</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {employees.map((employee) => (
              <div key={employee.employee_id} className="p-3 border rounded-lg flex items-center justify-between">
                <div>
                  <div className="font-medium">{employee.full_name}</div>
                  <div className="text-sm text-gray-600">{employee.employee_id} • {employee.department}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setFormData({...formData, employee_id: employee.employee_id});
                    setShowForm(true);
                  }}
                >
                  Create Review
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRPerformance;